import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, Lock, Loader2, ShieldCheck, Building2, Copy, CheckCircle2, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currencyUtils';

const FlutterwavePayment = ({ price, orderId, paymentMethod, numInstallments, currencyCode }) => {
    const navigate = useNavigate();
    const [paymentType, setPaymentType] = useState('card'); // card | bank_transfer
    const [step, setStep] = useState('card'); // card | bank_details | pin | otp
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [chargeId, setChargeId] = useState(null);

    // Card fields
    const [cardNumber, setCardNumber] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvv, setCvv] = useState('');

    // Auth fields
    const [pin, setPin] = useState('');
    const [otp, setOtp] = useState('');

    // Bank transfer fields
    const [bankDetails, setBankDetails] = useState(null);
    const [copied, setCopied] = useState(false);
    const pollRef = useRef(null);
    const transferRef = useRef(null);

    const formatCardNumber = (val) => {
        const v = val.replace(/\D/g, '').substring(0, 16);
        return v.replace(/(.{4})/g, '$1 ').trim();
    };

    const formatExpiry = (val) => {
        const v = val.replace(/\D/g, '').substring(0, 4);
        if (v.length > 2) return v.substring(0, 2) + '/' + v.substring(2);
        return v;
    };

    const parseExpiry = () => {
        const parts = expiry.split('/');
        return { month: parts[0] || '', year: parts[1] || '' };
    };

    useEffect(() => {
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const startPolling = (reference) => {
        if (pollRef.current) clearInterval(pollRef.current);
        let count = 0;
        pollRef.current = setInterval(async () => {
            count++;
            if (count > 60) {
                clearInterval(pollRef.current);
                setError('Transfer not detected yet. If you have transferred, please wait and refresh.');
                return;
            }
            try {
                const { data } = await axios.get(
                    `${process.env.REACT_APP_SERVER_URL}/api/payment/bank-transfer-status/${reference}`,
                    { withCredentials: true }
                );
                if (data.status === 'succeeded') {
                    clearInterval(pollRef.current);
                    toast.success('Payment confirmed!');
                    navigate(`/payment/callback?status=successful&tx_ref=${reference}`);
                }
            } catch (err) { /* silent */ }
        }, 5000);
    };

    const handleResponse = (data) => {
        // Bank transfer returns bank details for inline display
        if (data.requires_bank_transfer && data.bank_details) {
            setBankDetails(data.bank_details);
            transferRef.current = data.reference;
            setStep('bank_details');
            setLoading(false);
            startPolling(data.reference);
            return;
        }

        if (data.status === 'succeeded') {
            toast.success('Payment successful!');
            navigate(`/payment/callback?status=successful&tx_ref=${data.reference}`);
            return;
        }

        setChargeId(data.chargeId);
        const action = data.next_action?.type;

        if (action === 'requires_pin') {
            setStep('pin');
            setLoading(false);
        } else if (action === 'requires_otp') {
            setStep('otp');
            setLoading(false);
        } else if (action === 'redirect_url') {
            window.location.href = data.next_action.redirect_url.url;
        } else {
            setError('Unexpected response. Please try a different payment method.');
            setLoading(false);
        }
    };

    const submitCard = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        const { month, year } = parseExpiry();

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/payment/init-order`,
                {
                    orderId,
                    paymentMethod: paymentMethod || 'full',
                    numInstallments: numInstallments || null,
                    currency: currencyCode || 'NGN',
                    card: {
                        number: cardNumber.replace(/\s/g, ''),
                        expiry_month: month,
                        expiry_year: year,
                        cvv
                    }
                },
                { withCredentials: true }
            );
            handleResponse(data);
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Payment failed. Please try again.';
            const status = err.response?.status;
            const prefix = status === 400 ? 'Validation error' : status === 500 ? 'Server error' : 'Error';
            setError(`${prefix}: ${msg}`);
            toast.error(msg);
            setLoading(false);
        }
    };

    const submitBankTransfer = async () => {
        setError('');
        setLoading(true);
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/payment/init-order`,
                {
                    orderId,
                    paymentMethod: paymentMethod || 'full',
                    numInstallments: numInstallments || null,
                    currency: currencyCode || 'NGN',
                    paymentType: 'bank_transfer'
                },
                { withCredentials: true }
            );
            handleResponse(data);
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to generate bank details. Please try again.';
            const status = err.response?.status;
            const prefix = status === 400 ? 'Validation error' : status === 500 ? 'Server error' : 'Error';
            setError(`${prefix}: ${msg}`);
            toast.error(msg);
            setLoading(false);
        }
    };

    const submitPin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/payment/authorize`,
                { chargeId, type: 'pin', pin },
                { withCredentials: true }
            );
            setPin('');
            handleResponse(data);
        } catch (err) {
            setError(err.response?.data?.error || 'PIN verification failed.');
            setLoading(false);
        }
    };

    const submitOtp = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/payment/authorize`,
                { chargeId, type: 'otp', otp },
                { withCredentials: true }
            );
            handleResponse(data);
        } catch (err) {
            setError(err.response?.data?.error || 'OTP verification failed.');
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('Copied!');
        setTimeout(() => setCopied(false), 2000);
    };

    const displayPrice = formatCurrency(price, currencyCode);

    return (
        <div className="mt-4">
            {loading && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3 shadow-xl">
                        <Loader2 className="animate-spin text-black" size={36} />
                        <p className="text-sm font-medium text-gray-700">Processing payment...</p>
                    </div>
                </div>
            )}

            {error && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
                    {error}
                </div>
            )}

            {/* Payment method toggle - only show in initial state */}
            {step === 'card' && !chargeId && (
                <div className="flex mb-4 bg-gray-100 rounded-lg p-1">
                    <button
                        type="button"
                        onClick={() => { setPaymentType('card'); setStep('card'); setError(''); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                            paymentType === 'card' ? 'bg-white shadow-sm text-black' : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <CreditCard size={16} /> Card
                    </button>
                    <button
                        type="button"
                        onClick={() => { setPaymentType('bank_transfer'); setStep('card'); setError(''); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                            paymentType === 'bank_transfer' ? 'bg-white shadow-sm text-black' : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Building2 size={16} /> Bank Transfer
                    </button>
                </div>
            )}

            {/* Card form */}
            {paymentType === 'card' && step === 'card' && (
                <form onSubmit={submitCard} className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                        <div className="relative">
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="cc-number"
                                placeholder="0000 0000 0000 0000"
                                value={cardNumber}
                                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ffe11b] pr-10"
                                required
                            />
                            <CreditCard className="absolute right-3 top-3 text-gray-400" size={18} />
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="cc-exp"
                                placeholder="MM/YY"
                                value={expiry}
                                onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ffe11b]"
                                required
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                            <input
                                type="password"
                                inputMode="numeric"
                                autoComplete="cc-csc"
                                placeholder="•••"
                                maxLength={4}
                                value={cvv}
                                onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ffe11b]"
                                required
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={loading || cardNumber.replace(/\s/g, '').length < 15 || expiry.length < 5 || cvv.length < 3}
                        className="w-full py-3 bg-black text-white font-bold rounded-md hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    >
                        {loading ? (
                            <><Loader2 className="animate-spin" size={18} /> Processing...</>
                        ) : (
                            <><Lock size={16} /> Pay {displayPrice}</>
                        )}
                    </button>
                    <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
                        <ShieldCheck size={12} /> Secured by Flutterwave
                    </p>
                </form>
            )}

            {/* Bank Transfer initiate */}
            {paymentType === 'bank_transfer' && step === 'card' && (
                <div className="space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-3">
                            <Building2 className="text-blue-600 mt-0.5" size={20} />
                            <div>
                                <p className="text-sm font-medium text-blue-900">Bank Transfer</p>
                                <p className="text-xs text-blue-700 mt-1">
                                    A temporary account will be generated for you. Transfer the exact amount to complete your payment.
                                </p>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={submitBankTransfer}
                        disabled={loading}
                        className="w-full py-3 bg-black text-white font-bold rounded-md hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    >
                        {loading ? (
                            <><Loader2 className="animate-spin" size={18} /> Generating account...</>
                        ) : (
                            <><Building2 size={16} /> Get Bank Details &middot; {displayPrice}</>
                        )}
                    </button>
                    <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
                        <ShieldCheck size={12} /> Secured by Flutterwave
                    </p>
                </div>
            )}

            {/* Bank transfer details */}
            {step === 'bank_details' && bankDetails && (
                <div className="space-y-4">
                    <div className="text-center mb-2">
                        <Building2 className="mx-auto text-blue-600 mb-2" size={32} />
                        <p className="text-sm font-medium text-gray-800">Transfer to the account below</p>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500 uppercase tracking-wider">Bank</span>
                            <span className="text-sm font-semibold text-gray-800">{bankDetails.bank_name}</span>
                        </div>
                        <div className="border-t border-gray-200"></div>
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500 uppercase tracking-wider">Account Number</span>
                            <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-gray-900 font-mono">{bankDetails.account_number}</span>
                                <button
                                    onClick={() => copyToClipboard(bankDetails.account_number)}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                >
                                    {copied ? <CheckCircle2 size={16} className="text-green-500" /> : <Copy size={16} className="text-gray-400" />}
                                </button>
                            </div>
                        </div>
                        <div className="border-t border-gray-200"></div>
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500 uppercase tracking-wider">Amount</span>
                            <span className="text-lg font-bold text-green-700">₦{bankDetails.amount?.toLocaleString()}</span>
                        </div>
                    </div>

                    {bankDetails.expires_at && (
                        <div className="flex items-center justify-center gap-1 text-xs text-amber-600">
                            <Clock size={12} />
                            <span>This account expires at {new Date(bankDetails.expires_at).toLocaleTimeString()}</span>
                        </div>
                    )}

                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                        <Loader2 size={14} className="animate-spin" />
                        <span>Waiting for your transfer...</span>
                    </div>

                    <p className="text-xs text-gray-400 text-center">
                        Transfer the exact amount. Payment will be confirmed automatically.
                    </p>
                </div>
            )}

            {step === 'pin' && (
                <form onSubmit={submitPin} className="space-y-4">
                    <div className="text-center mb-2">
                        <Lock className="mx-auto text-gray-600 mb-2" size={32} />
                        <p className="text-sm text-gray-600 font-medium">Enter your card PIN to continue</p>
                    </div>
                    <input
                        type="password"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="Enter PIN"
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                        className="w-full px-3 py-3 border border-gray-300 rounded-md text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-[#ffe11b]"
                        autoFocus
                        required
                    />
                    <button
                        type="submit"
                        disabled={loading || pin.length < 4}
                        className="w-full py-3 bg-black text-white font-bold rounded-md hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {loading ? <><Loader2 className="animate-spin" size={18} /> Verifying...</> : 'Continue'}
                    </button>
                </form>
            )}

            {step === 'otp' && (
                <form onSubmit={submitOtp} className="space-y-4">
                    <div className="text-center mb-2">
                        <ShieldCheck className="mx-auto text-green-500 mb-2" size={32} />
                        <p className="text-sm text-gray-600 font-medium">Enter the OTP sent to your phone or email</p>
                    </div>
                    <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="Enter OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                        className="w-full px-3 py-3 border border-gray-300 rounded-md text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-[#ffe11b]"
                        autoFocus
                        required
                    />
                    <button
                        type="submit"
                        disabled={loading || otp.length < 4}
                        className="w-full py-3 bg-black text-white font-bold rounded-md hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {loading ? <><Loader2 className="animate-spin" size={18} /> Verifying...</> : 'Verify & Pay'}
                    </button>
                </form>
            )}
        </div>
    );
};

export default FlutterwavePayment;